import apiClient from './apiClient';
import { AuthResponse, LoginRequest, ChangePasswordRequest } from '../types';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_DATA_KEY } from '../config/constants';

// Helper function to decode JWT
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

export const authService = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    console.log('[authService] Starting login...');
    const response = await apiClient.getAuthClient().post('/auth/login', credentials);
    console.log('[authService] Login response:', response.data);
    
    // Transform backend response to match frontend types
    const { accessToken, refreshToken, username, role, expiresAt } = response.data;
    
    // Decode JWT to extract sellerId
    const decodedToken = decodeJWT(accessToken);
    console.log('[authService] Decoded token:', decodedToken);
    const sellerId = decodedToken?.SellerId ? parseInt(decodedToken.SellerId) : undefined;
    console.log('[authService] SellerId:', sellerId);
    
    const user: any = {
      id: decodedToken?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] ? 
          parseInt(decodedToken["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"]) : 0,
      username,
      roleId: 0,
      role: {
        id: 0,
        name: role,
        isActive: true
      },
      sellerId,
      isActive: true,
      createdAt: new Date().toISOString()
    };
    
    console.log('[authService] User object before seller fetch:', user);
    
    // Store tokens first so subsequent API calls work
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    console.log('[authService] Tokens stored');
    
    // Fetch seller information if sellerId exists
    if (sellerId) {
      try {
        console.log('[authService] Fetching seller information...');
        const sellerResponse = await apiClient.getSellerClient().get('/sellers/me');
        console.log('[authService] Seller response:', sellerResponse.data);
        user.seller = sellerResponse.data;
      } catch (error) {
        console.error('[authService] Error fetching seller information:', error);
      }
    }
    
    console.log('[authService] Final user object:', user);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
    console.log('[authService] User data stored in localStorage');
    
    return { accessToken, refreshToken, user };
  },

  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    await apiClient.getAuthClient().post('/auth/change-password', data);
  },

  logout: () => {
    apiClient.logout();
  },

  getCurrentUser: () => {
    const userData = localStorage.getItem(USER_DATA_KEY);
    if (!userData || userData === 'undefined') {
      return null;
    }
    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem(USER_DATA_KEY);
      return null;
    }
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  validateToken: async (): Promise<void> => {
    // Validate token by calling /users/me endpoint
    // This works for all roles and confirms user is active and has valid permissions
    // Will trigger token refresh if needed via the interceptor
    await apiClient.getSellerClient().get('/users/me');
  },

  sendSetupEmail: async (userId: number): Promise<void> => {
    await apiClient.getAuthClient().post('/auth/send-setup-email', { userId });
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.getAuthClient().post('/auth/forgot-password', { email });
  },

  validatePasswordToken: async (token: string): Promise<{ valid: boolean; tokenType: string }> => {
    const response = await apiClient.getAuthClient().get('/auth/validate-token', { params: { token } });
    return response.data;
  },

  setPassword: async (token: string, newPassword: string): Promise<AuthResponse> => {
    const response = await apiClient.getAuthClient().post('/auth/set-password', { token, newPassword });
    const { accessToken, refreshToken, username, role } = response.data;
    const decodedToken = decodeJWT(accessToken);
    const sellerId = decodedToken?.SellerId ? parseInt(decodedToken.SellerId) : undefined;
    const user: any = {
      id: decodedToken?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"]
        ? parseInt(decodedToken["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"])
        : 0,
      username,
      roleId: 0,
      role: { id: 0, name: role, isActive: true },
      sellerId,
      isActive: true,
      createdAt: new Date().toISOString()
    };
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    if (sellerId) {
      try {
        const sellerResponse = await apiClient.getSellerClient().get('/sellers/me');
        user.seller = sellerResponse.data;
      } catch {}
    }
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
    return { accessToken, refreshToken, user };
  },
};
